import {
  action,
  computed,
  observable,
  runInAction
} from 'mobx';
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  AUTH_USER_FORBIDDEN,
  AUTH_USER_INCORRECT,
  FILE,
  FILES_REMOVE_SUCCESS,
  FILES_DOWNLOAD_SUCCESS,
  //FILES_LIST_ITEMS_PER_PAGE,
  MESSAGE_CREATED,
  SCROLL_EVENT,
  SORT_DATE_CREATED_DESC,
  URL,
} from '../../constants/constants';
import FilesService from '../services/files.service';
import AmazonService from '../services/amazon.service';
import commonStore from '../../common/stores/common.store';
import authenticationStore from './authentication.store';
import {
  // addItemsToArrayIfTheyDontExist,
  isVericalScrollBarAtBottom
} from '../../common/helpers/common.helpers';

export class FilesStore {
  @observable files = [];

  @observable filesContinerRef = null;

  @observable isDataLoading = false;

  @computed get numberOfFiles() {
    return this.files.length;
  }

  /**
   * Returns the count of selected files.
   * */
  @computed get numberOfSelectedFiles() {
    return this.files.filter(file => file.selected).length;
  }

  /**
   * Sets status for data loading flag.
   * @param {bool} status - The status.
   * */
  @action setIsDataLoadingStatus = (isDataLoading) => {
    this.isDataLoading = isDataLoading;
  };

  /**
   * Sets the file container Ref.
   * */
  @action setFilesContinerRef = (fileRef) => {
    this.filesContinerRef = fileRef;
  };


  /**
   * Sets the file container Ref.
   * */
  @action addEventScroll = () => {
    this.filesContinerRef.addEventListener(SCROLL_EVENT, () => {
      this.handleScroll();
    });
  };

  /**
   * Handle Scroll by loading more items when user scroll is at the end of the list.
   * */
  @action handleScroll = async () => {
    if (!this.isDataLoading && isVericalScrollBarAtBottom(this.filesContinerRef)) {
      this.setIsDataLoadingStatus(true);
      await this.loadFiles();
      this.setIsDataLoadingStatus(false);
    }
  };

  /**
   * Loads the files.
   * */
  @action loadFiles = async () => {
    try {
      //const currentPage = Math.floor(this.numberOfFiles / FILES_LIST_ITEMS_PER_PAGE) + 1;
      // TODO : In future, remove page approach.No paging is needed.
      //        Just send in header the file(s) id(s) returned in previous call.Send in request,the number of items to return.
      //const newItems = await FilesService.load({ page: currentPage, sort: SORT_DATE_CREATED_DESC });

      //Fix to bug that did not load the file name with the extension without reloading the files
      const newFiles = await FilesService.load({sort: SORT_DATE_CREATED_DESC, limit: 0});

      runInAction(() => {
        //const filesUpdated = addItemsToArrayIfTheyDontExist(this.files, newItems);
        //this.files = [...filesUpdated];
        this.files = newFiles;

      });
    } catch (e) {
      runInAction(() => {
        this.files = [];
        if (e.message === AUTH_USER_INCORRECT
          || e.message === AUTH_USER_FORBIDDEN) {
          authenticationStore.clearAuthenticationLocalStore();
        }
      });
    }
  };

  /**
   * Filters the files.
   * @param {string} query - The search query.
   * */
  @action searchFiles = async (query) => {
    commonStore.showSpinner();
    try {
      const files = await FilesService.load(query);

      runInAction(() => {
        this.files = files.filter(file => file.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
        commonStore.hideSpinner();
      });
    } catch (e) {
      runInAction(() => {
        this.files = [];
        commonStore.hideSpinner();
      });
    }
  };

  /**
   * Uploads an audio file.
   * @param {object} file - The file.
   * @param {object}message - The new created message.
   * */
  @action uploadFile = async (file, message) => {
    try {
      const formData = new FormData(); // eslint-disable-line no-undef

      Object.entries(message.upload).forEach(([key, value]) => {
        if (key !== URL) {
          formData.append(key, value);
        }
      });
      formData.append(FILE, file);

      await FilesService.upload(formData, message.upload.url);
      this.files = this.files.map(item => (item.id === message.id ? { ...item, isUploading: false } : item));
    } catch (e) {
      runInAction(() => {
        commonStore.show(e.message, ALERT_ERROR);
      });
    }
  };

  /**
   * Creates a new message.
   * @param {object} file - The file.
   * */
  @action createMessage = async (file) => {
    commonStore.showSpinner();
    try {
      const { name } = file;
      const createdMessage = await FilesService.create({ name });

      if (createdMessage) {
        this.files = [{ ...createdMessage, isUploading: true }, ...this.files];
        commonStore.show(MESSAGE_CREATED, ALERT_SUCCESS);
        commonStore.hideSpinner();
        this.uploadFile(file, createdMessage);
        return createdMessage;
      }
    } catch (e) {
      runInAction(() => {
        commonStore.show(e.message, ALERT_ERROR);
        commonStore.hideSpinner();
      });
    }
    return null;
  };

  /**
   * Removes new message from the list.
   * @param {number} id - The message id.
   * */
  @action removeFile = (id) => {
    this.files = this.files.filter(file => file.id !== id);
  };


  /**
   * Find and update the file name
   * @param {number} id - The message id.
   * @param {string} name - The file name.
   * */
  @action setFileName = (id, name) => {
    const fileFound = this.files.find(file => file.id === Number(id));
    if (fileFound) {
      fileFound.name = name;
    }
  };


  /**
   * Find and update the file selection state
   * @param {number} id - The file id.
   * @param {string} isSelected - The new state.
   * */

  @action setFileSelection = (id, isSelected) => {
    const fileFound = this.files.find(file => file.id === Number(id));
    if (fileFound) {
      fileFound.selected = isSelected;
    }
  };

  /**
   * Returns the selectected files
   * */
  @action filesSelected = () => this.files.filter(file => file.selected);

  /**
   * Reset Store values
   * */
  @action reset = () => {
    this.files = [];
    this.isDataLoading = false;
  };

  /**
   * Unselects the checked files
   * */
  @action resetFilesSelection = () => {
    this.files = this.files.map(({ selected, ...file }) => file);
  };

  /**
   * Select all files
   * */
  @action allFilesSelection = () => {
  
    for (const e of this.files) {  
      const fileFound = this.files.find(file => file.id === Number(e.id));
      if (fileFound) {
        fileFound.selected = true;
      }
    };

  };


  @action removeFiles = async () => {

    commonStore.showSpinner();
    try {
      const filesSelected = this.filesSelected();
      if (filesSelected) {
        
        for (const e of filesSelected) {
          const fileFound = filesSelected.find(file => file.id === Number(e.id));
          if (fileFound) {
            
            //delete files
            await FilesService.remove(fileFound.id);
            this.removeFile(fileFound.id);
          }

        this.resetFilesSelection();
        }

      }
      commonStore.show(FILES_REMOVE_SUCCESS, ALERT_SUCCESS);
      commonStore.hideSpinner();

    } catch (e) {
      commonStore.show(e.message, ALERT_ERROR);
      commonStore.hideSpinner();
    }
  };


  /**
   * Triggers message zip and download files to browser
   */
  @action downloadFilesFromAmazon = async () => {

    commonStore.showSpinner();
    try {
      const filesSelected = this.filesSelected();
      if (filesSelected) {
        await AmazonService.downloadZip(filesSelected);
        this.resetFilesSelection();
        commonStore.show(FILES_DOWNLOAD_SUCCESS, ALERT_SUCCESS);
      }
      commonStore.hideSpinner();

    } catch (e) {
      commonStore.show(e.message, ALERT_ERROR);
      commonStore.hideSpinner();
    }
  };
}

export default new FilesStore();



// WEBPACK FOOTER //
// ./src/messages/stores/files.store.js